// useSetupCompleteness — shared hook that tells the dashboard / route gates
// whether the active school's mandatory setup is fully filled in, and
// whether any "Daily entries exist but Fund Management is empty for that
// month" gaps exist.
//
// Why a hook (not a one-shot fetch):
//   - SchoolDashboard, AuthGate route-guards and the upcoming amber-chips
//     all need the same answer. Centralising the fetch keeps cache misses
//     and 4× repeat network calls out of the picture.
//   - Re-runs only when the active schoolId changes — not on every render.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { vendorApi, cchApi, enrollmentApi, fundApi, dailyApi } from "@/lib/setupApi";
import { profileApi } from "@/lib/profilesApi";
import { computeTabStatus, pendingReason, SETUP_TABS, TAB_LABELS } from "@/lib/setupGuide";
import { MONTH_LIST } from "@/lib/mealPlan";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Returns YYYY-MM-DD of the first/last day of a given (year, month) where
// month is 1-12. We use these to scope the "daily entries this month"
// query so we don't pull every record the school has ever saved.
function monthRange(year, month) {
  const from = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
}

export function useSetupCompleteness(schoolId) {
  const [profile, setProfile] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [cchs, setCchs] = useState([]);
  const [enrolls, setEnrolls] = useState([]);
  const [fundBalances, setFundBalances] = useState([]);
  const [recentEntries, setRecentEntries] = useState([]);
  // iter123 — backend-authoritative status (consults admin's field_config).
  const [serverStatus, setServerStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/setup-status`);
        if (!cancelled) setServerStatus(r.data);
      } catch (e) {
        if (!cancelled) setServerStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pList, v, c, fb] = await Promise.all([
          profileApi.list(),
          vendorApi.list(),
          cchApi.list(),
          fundApi.list(schoolId),
        ]);
        if (cancelled) return;
        const p = (pList || []).find((x) => x.id === schoolId) || null;
        setProfile(p);
        setVendors((v || []).filter((x) => {
          const tags = Array.isArray(x.schoolIds) ? x.schoolIds : [];
          return x.schoolId === schoolId || tags.includes(schoolId);
        }));
        setCchs((c || []).filter((x) => {
          const tags = Array.isArray(x.schoolIds) ? x.schoolIds : [];
          return x.schoolId === schoolId || tags.includes(schoolId);
        }));
        setFundBalances(fb || []);
        // Pull last ~90 days of daily entries to detect "fund balance missing
        // for months that already have meals logged".
        try {
          const today = new Date();
          const past = new Date(today);
          past.setDate(past.getDate() - 90);
          const fromDate = past.toISOString().slice(0, 10);
          const toDate = today.toISOString().slice(0, 10);
          const entries = await dailyApi.list({ schoolId, fromDate, toDate });
          if (!cancelled) setRecentEntries(Array.isArray(entries) ? entries : []);
        } catch { if (!cancelled) setRecentEntries([]); }
        // Enrollment is per-school.
        try {
          const e = await enrollmentApi.list(schoolId);
          if (!cancelled) setEnrolls(Array.isArray(e) ? e : []);
        } catch { if (!cancelled) setEnrolls([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId]);

  const status = useMemo(
    () => computeTabStatus({ profile, vendors, cchs, enrolls, serverSections: serverStatus?.sections }),
    [profile, vendors, cchs, enrolls, serverStatus],
  );

  const missingTabs = useMemo(
    () => SETUP_TABS.filter((t) => !status[t]),
    [status],
  );

  const isComplete = missingTabs.length === 0 && !!profile;

  const missingReasons = useMemo(() => {
    const map = {};
    missingTabs.forEach((t) => {
      map[t] = pendingReason(t, { profile, vendors, cchs, serverMissingBySection: serverStatus?.missingBySection });
    });
    return map;
  }, [missingTabs, profile, vendors, cchs, serverStatus]);

  // List of (year, month) keys where Daily Entries exist BUT no fund-balance
  // record is saved. Used to surface the "Opening balance missing for
  // {Month}" amber chip on the dashboard. Capped to the last 3 months for
  // readability — older months will surface via the regular Fund Management
  // ledger.
  const missingFundsMonths = useMemo(() => {
    if (!recentEntries.length) return [];
    const fundKeys = new Set((fundBalances || []).map((b) => `${b.year}-${b.month}`));
    const monthsWithEntries = new Map();
    recentEntries.forEach((e) => {
      if (!e?.date || typeof e.date !== "string") return;
      const [y, m] = e.date.split("-");
      const key = `${Number(y)}-${Number(m)}`;
      if (!monthsWithEntries.has(key)) {
        monthsWithEntries.set(key, { year: Number(y), month: Number(m) });
      }
    });
    const out = [];
    monthsWithEntries.forEach((info, key) => {
      if (!fundKeys.has(key)) {
        const label = MONTH_LIST.find((mm) => mm.value === info.month)?.label || String(info.month);
        out.push({ ...info, label: `${label} ${info.year}` });
      }
    });
    // Sort newest first, cap to 3.
    out.sort((a, b) => (b.year - a.year) || (b.month - a.month));
    return out.slice(0, 3);
  }, [recentEntries, fundBalances]);

  // Friendly Odia summary of the missing setup pieces — used by the amber
  // chip on the dashboard.
  const setupSummary = useMemo(() => {
    if (isComplete) return null;
    const labels = missingTabs.map((t) => TAB_LABELS[t]?.od || TAB_LABELS[t]?.en || t);
    return labels.join(", ");
  }, [isComplete, missingTabs]);

  return {
    loading,
    profile,
    vendors,
    cchs,
    enrolls,
    fundBalances,
    recentEntries,
    status,
    isComplete,
    missingTabs,
    missingReasons,
    missingFundsMonths,
    setupSummary,
  };
}
